
import {Link} from 'react-router-dom'
import { useEffect, useState } from "react"
import { Teacher } from "models/model"
import { TeacherDelete, TeacherGetPaging } from "services/teacherService"
import ConfirmDialog from "popup/ConfirmDialog"

import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { fetchLicenseClasses } from 'store/licenseSlice'
import {toast} from "react-hot-toast";
import ErrorModal from "popup/ErrorModal";
import {fetchPartners} from "store/partnerSlice";
import LoadingOverlay from "components/Static/LoadingOverlay";
import Pagination from "components/Pagination";
import { Edit, Plus, Trash } from 'lucide-react';


export default function TeachersPage() {
    const dispatch: AppDispatch = useDispatch()

    const licenseClasses = useSelector((state: RootState) => state.licenseClasses.items)
    const licenseLoading = useSelector((state: RootState) => state.licenseClasses.loading)
    const partners = useSelector((state: RootState) => state.partners.items)
    const [errorModalOpen, setErrorModalOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const [teachers, setTeachers] = useState<Teacher[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [isConfirmOpen, setIsConfirmOpen] = useState(false)
    const [selectedTeacherId, setSelectedTeacherId] = useState<string | null>(null)
    const [searchText, setSearchText] = useState<string>("")
    const [searchLicenseClass, setSearchLicenseClass] = useState<string>("")
    const [searchPartner, setSearchPartner] = useState<string>("")

    const [pagination, setPagination] = useState({
        pageIndex: 1,
        pageSize: 10,
        totalRecords: 0,
        totalPages: 1
    })

    const openConfirm = (teacherId: string) => {
        setSelectedTeacherId(teacherId)
        setIsConfirmOpen(true)
    }

    const handleDelete = async () => {
        if (selectedTeacherId === null) return
        try {
            setIsLoading(true)
            await TeacherDelete(selectedTeacherId)
            setTeachers(prev => prev.filter(t => t.id !== selectedTeacherId))
            // Refresh pagination after deletion
            toast.success("Xóa giáo viên thành công");
            await fetchTeachers(pagination.pageIndex, true)
        } catch (error : any) {
            const errorMsg = error?.responseException?.exceptionMessage
                || error?.message
                || "Lỗi";
            setErrorMessage(errorMsg);
            setErrorModalOpen(true);
        } finally {
            setIsLoading(false)
        }
    }

    const handleSearch = () => {
        // Reset to first page when searching
        fetchTeachers(1)
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSearch()
        }
    }

    const fetchTeachers = async (pageIndex: number, keepPageIndex: boolean = false) => {
        setIsLoading(true)
        try {
            const res = await TeacherGetPaging({
                pageIndex: keepPageIndex ? pagination.pageIndex : pageIndex,
                pageSize: pagination.pageSize,
                freeTextSearch: searchText,
                licenseClassId: searchLicenseClass || null,
                partnerId: searchPartner || null
            })
            setTeachers(res.result.items)
            setPagination(prev => ({
                ...prev,
                pageIndex: keepPageIndex ? prev.pageIndex : pageIndex,
                totalRecords: res.result.totalRecord,
                totalPages: Math.ceil(res.result.totalRecord / pagination.pageSize)
            }))
        } catch (error) {
            console.error('Failed to fetch teachers:', error)
        } finally {
            setIsLoading(false)
        }
    }

    const handlePageChange = (newPageIndex: number) => {
        if (newPageIndex < 1 || newPageIndex > pagination.totalPages) return
        fetchTeachers(newPageIndex)
    }

    useEffect(() => {
        if (licenseClasses.length === 0) {
            dispatch(fetchLicenseClasses())
        }
        if (partners.length === 0) {
            dispatch(fetchPartners())
        }
        fetchTeachers(1)
    }, [searchLicenseClass, searchPartner])

    return (
        <div className="bg-white rounded-lg shadow-md p-6">
            <h1 className="text-3xl font-bold text-gray-800">Quản lý Giáo viên</h1>
            <div className="md:flex md:justify-between md:items-center mb-6 mt-6">
                <Link
                    to="/teachers/new"
                    className="flex items-center px-4 py-2 mb-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                    <Plus className="mr-2" />
                    Thêm giáo viên mới
                </Link>

                <div className="md:flex md:justify-start md:items-center">

                    <div className="md:flex md:items-center mb-3 md:mr-4">
                        <select
                            className="md:min-w-max w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
                            value={searchLicenseClass}
                            onChange={(e) => setSearchLicenseClass(e.target.value)}
                            disabled={isLoading || licenseLoading}
                        >
                            <option value="">Tất cả hạng đào tạo</option>
                            {licenseClasses.map((lc) => (
                                <option key={lc.id} value={lc.id}>
                                    {lc.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="md:flex md:items-center mb-3 md:mr-4">
                        <select
                            className="md:min-w-max w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
                            value={searchPartner}
                            onChange={(e) => setSearchPartner(e.target.value)}
                        >
                            <option value="">Tất cả đơn vị</option>
                            {partners.map((lc) => (
                                <option key={lc.id} value={lc.id}>
                                    {lc.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="flex items-center space-x-2 mb-3">
                        <input
                            type="text"
                            placeholder="Tìm kiếm giáo viên..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            onKeyDown={handleKeyDown}
                            className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
                            disabled={isLoading}
                                                                                                          />
                        <button
                            onClick={handleSearch}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                            disabled={isLoading}
                        >
                            Tìm
                        </button>
                    </div>
                </div>
            </div>

            {isLoading ? <LoadingOverlay message="Đang tải dữ liệu..."/> : (
                <div className="mx-auto">
                    <div className="grid grid-cols-1 gap-8">
                        <div className="bg-white rounded-lg shadow-md overflow-hidden">
                            <div className="w-full overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">STT</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Họ
                                            và tên
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Số
                                            điện thoại
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hạng
                                            đào tạo
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Đơn
                                            vị liên kết
                                        </th>
                                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Thao
                                            tác
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                    {teachers.map((teacher, index) => (
                                        <tr key={teacher.id}>
                                            <td className="px-6 py-4 whitespace-nowrap text-center text-gray-500">
                                                {(pagination.pageIndex - 1) * pagination.pageSize + index + 1}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="font-medium text-gray-900">{teacher.fullName}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                                                {teacher.mobile || 'N/A'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                                                {teacher.licenseClassName}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap  text-gray-500">
                                                {teacher.partnerName || "Không"}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                                                <Link
                                                    to={`/teachers/${teacher.id}/edit`}
                                                    className="text-blue-600 hover:text-blue-900 mr-4"
                                                >
                                                    <Edit className="inline mr-1"/> Sửa
                                                </Link>
                                                <button
                                                    // @ts-ignore
                                                    onClick={() => openConfirm(teacher.id)}
                                                    className="text-red-600 hover:text-red-900"
                                                >
                                                    <Trash className="inline mr-1"/> Xóa
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Pagination */}
                        <Pagination
                            pagination={pagination}
                            isLoading={isLoading}
                            onPageChange={handlePageChange}
                            label="giáo viên"
                        />
                    </div>
                </div>

            )}

            <ErrorModal
                isOpen={errorModalOpen}
                message={errorMessage}
                onClose={() => setErrorModalOpen(false)}
            />

            <ConfirmDialog
                isOpen={isConfirmOpen}
                title="Xác nhận xóa"
                description="Bạn có chắc chắn muốn xóa giáo viên này không?"
                onClose={() => setIsConfirmOpen(false)}
                onConfirm={handleDelete}
            />
        </div>
    )
}